import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, Lock } from 'lucide-react';
import { cfmt, pfmt } from '../../lib/api';

/**
 * Preview page — before signup. Top-1 fully revealed, 2-10 locked/blurred.
 * On unlock, the parent replaces this with FullResults.
 */
export default function MatchPreview({ preview, onUnlockClick }) {
    if (!preview || !preview.top_match) return null;
    const top = preview.top_match;
    const locked = preview.locked_matches || [];

    return (
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 lg:py-20" data-testid="match-preview">
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.22, 0.61, 0.36, 1] }}
            >
                <div className="text-[11px] tracking-[0.24em] uppercase text-neutral-500 font-mono-data flex items-center gap-3">
                    <span className="inline-block w-6 h-px bg-neutral-400" />
                    Preliminary results · {preview.total} matches scored
                </div>
                <h2 className="font-editorial mt-6 leading-[1.03]" style={{ fontSize: 'clamp(36px, 4.6vw, 60px)', letterSpacing: '-0.02em' }}>
                    Your top match is <span className="italic text-neutral-500">below.</span>
                </h2>
                <p className="mt-5 font-editorial text-neutral-600 max-w-2xl" style={{ fontSize: '18px', lineHeight: 1.55 }}>
                    Nine more neighbourhoods scored above 60%. Create an account to see the full ranking, the
                    percentage on each, and the reasons behind every score.
                </p>
            </motion.div>

            {/* Top match — full card */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 0.61, 0.36, 1] }}
                className="mt-12 border border-neutral-200 bg-white"
                data-testid="match-preview-top"
            >
                <div className="grid grid-cols-1 lg:grid-cols-12">
                    {/* image */}
                    <div className="lg:col-span-5 relative h-72 lg:h-auto lg:min-h-[420px] bg-neutral-100">
                        {top.hero_image ? (
                            <img src={top.hero_image} alt={top.name} className="absolute inset-0 w-full h-full object-cover" />
                        ) : null}
                        <div className="absolute top-4 left-4 bg-brand-navy text-white px-3 py-1.5 text-[11px] tracking-[0.22em] uppercase font-mono-data">
                            Rank 01 · {top.region}
                        </div>
                    </div>

                    {/* content */}
                    <div className="lg:col-span-7 p-8 lg:p-12 flex flex-col">
                        <div className="flex items-baseline justify-between gap-6">
                            <div>
                                <div className="overline">Best fit</div>
                                <div className="font-editorial mt-3 tracking-editorial leading-tight" style={{ fontSize: '44px', letterSpacing: '-0.02em' }}>
                                    {top.name}
                                </div>
                                <div className="mt-2 text-[13px] tracking-[0.18em] uppercase text-neutral-500 font-mono-data">
                                    {top.city} · {top.region}
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="font-editorial tabular text-black" style={{ fontSize: '64px', lineHeight: 1, letterSpacing: '-0.03em' }}>
                                    {top.match_percentage}
                                    <span className="text-[28px] text-neutral-400">%</span>
                                </div>
                                <div className="text-[10px] tracking-[0.24em] uppercase text-neutral-500 font-mono-data mt-1">
                                    Match
                                </div>
                            </div>
                        </div>

                        {top.tagline && (
                            <p className="mt-6 text-[15px] text-neutral-700 font-editorial italic leading-relaxed">
                                &ldquo;{top.tagline}&rdquo;
                            </p>
                        )}

                        <div className="mt-8">
                            <div className="text-[11px] tracking-[0.22em] uppercase text-neutral-500 font-mono-data">Why it fits you</div>
                            <ul className="mt-4 space-y-3">
                                {top.reasons.map((r, i) => (
                                    <li key={i} className="text-[14px] text-black leading-relaxed flex gap-3">
                                        <span className="font-mono-data text-neutral-400 tabular">0{i + 1}</span>
                                        <span>{r}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {top.concern && (
                            <div className="mt-6 pt-5 border-t border-neutral-200">
                                <div className="text-[11px] tracking-[0.22em] uppercase text-neutral-500 font-mono-data">Honest trade-off</div>
                                <p className="mt-2 text-[13.5px] text-neutral-700 leading-relaxed">{top.concern}</p>
                            </div>
                        )}

                        <div className="mt-8 flex items-center gap-6 pt-6 border-t border-neutral-200 font-mono-data text-[12px] text-neutral-500">
                            <span>Median <span className="text-black">{cfmt(top.median_price)}</span></span>
                            <span>5Y f&apos;cast <span className="text-[#2F6B47]">+{pfmt(top.forecast_5yr_pct)}</span></span>
                            <Link to={`/neighbourhoods/${top.slug}`} className="ml-auto flex items-center gap-1 text-black link-editorial">
                                Explore <ArrowUpRight className="w-3.5 h-3.5" />
                            </Link>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Locked matches — blurred rank strip */}
            <div className="mt-16">
                <div className="flex items-baseline justify-between mb-6">
                    <div>
                        <div className="overline">The other nine</div>
                        <div className="font-editorial mt-3 tracking-editorial leading-tight" style={{ fontSize: '30px' }}>
                            Ranks 02 — 10, locked.
                        </div>
                    </div>
                    <button
                        onClick={onUnlockClick}
                        data-testid="match-preview-unlock-btn"
                        className="inline-flex items-center gap-2 bg-brand-navy text-white px-6 py-3 text-[13.5px] font-medium hover:bg-neutral-800"
                       
                    >
                        <Lock className="w-3.5 h-3.5" /> Unlock full ranking
                    </button>
                </div>
                <div className="border-l border-t border-neutral-200">
                    {locked.map((l, i) => (
                        <div
                            key={l.slug}
                            className="border-r border-b border-neutral-200 grid grid-cols-12 items-center py-5 px-6 bg-white hover:bg-[#FAFAF8] cursor-pointer"
                            onClick={onUnlockClick}
                            data-testid={`match-locked-row-${i}`}
                        >
                            <div className="col-span-1 font-mono-data text-[13px] text-neutral-400 tabular">
                                {String(i + 2).padStart(2, '0')}
                            </div>
                            <div className="col-span-7 font-editorial text-[22px] tracking-editorial select-none" style={{ filter: 'blur(6px)' }}>
                                Neighbourhood name — locked
                            </div>
                            <div className="col-span-3 font-mono-data tabular text-neutral-600 text-[14px]">
                                {l.match_percentage}% match
                            </div>
                            <div className="col-span-1 flex justify-end">
                                <Lock className="w-4 h-4 text-neutral-400" />
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-10 text-center">
                    <button
                        onClick={onUnlockClick}
                        data-testid="match-preview-unlock-cta"
                        className="inline-flex items-center gap-2 bg-brand-navy text-white px-8 py-4 text-[14px] font-medium hover:bg-neutral-800"
                        style={{ letterSpacing: '0.02em' }}
                    >
                        Unlock the full ranked report — free
                        <ArrowUpRight className="w-4 h-4" />
                    </button>
                    <div className="mt-4 text-[11px] uppercase tracking-[0.22em] text-neutral-400 font-mono-data">
                        Google or email · 30 seconds · No credit card
                    </div>
                </div>
            </div>
        </div>
    );
}
